import { Card } from "@/components/ui/Card";
import { GlobalCTA } from "@/components/ui/CTA";
import { List } from "@/components/ui/List";
import { PageHero } from "@/components/ui/PageHero";
import { Section } from "@/components/ui/Section";
import {
  governanceChecks,
  growthLoop,
  workflowDefinition,
  workflowSteps,
} from "@/content/workflow";

export default function HowItWorksPage() {
  return (
    <>
      <PageHero
        eyebrow="How it works"
        title="From evidence to governed creative growth"
        description={workflowDefinition}
      />

      <Section
        title="The creative growth loop"
        lead="The product loop connects observation, memory, hypothesis, creation, approval, distribution, measurement, and learning."
        tone="surface"
      >
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {growthLoop.map((item, index) => (
            <Card
              key={item.title}
              eyebrow={String(index + 1).padStart(2, "0")}
              title={item.title}
              description={item.description}
            />
          ))}
        </div>
      </Section>

      <Section
        title="The seven-step mission workflow"
        lead="Every step has a purpose, an output, and a record that the next step can use."
        tone="paper"
      >
        <div className="mt-10 space-y-6">
          {workflowSteps.map((step, index) => (
            <article key={step.id} id={step.id} className="surface-card p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-4">
                <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-900 text-lg font-bold text-mint">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="text-2xl font-bold">{step.title}</h3>
              </div>
              <p className="mt-4 max-w-4xl leading-relaxed text-ink-soft">{step.description}</p>
              {step.inputs ? (
                <div className="mt-5">
                  <h4 className="text-sm font-bold uppercase tracking-wide text-mint-dark">
                    Accepted inputs
                  </h4>
                  <List items={step.inputs} variant="plain" className="mt-3" />
                </div>
              ) : null}
              {step.outputs ? (
                <div className="mt-5">
                  <h4 className="text-sm font-bold uppercase tracking-wide text-mint-dark">
                    Outputs
                  </h4>
                  <List items={step.outputs} variant="plain" className="mt-3" />
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </Section>

      <Section
        title="Governed execution checks"
        lead="Agent work is constrained by visible context, evidence, permissions, and reviewable execution plans."
        tone="surface"
      >
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {governanceChecks.map((item) => (
            <Card key={item.title} title={item.title} description={item.description} />
          ))}
        </div>
      </Section>

      <Section
        title="What stays reviewable"
        lead="The workflow is designed for auditability, not just generation."
        tone="paper"
      >
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Decision context",
              description:
                "The goal, evidence, assumptions, brief, and chosen creative remain together.",
            },
            {
              title: "Asset lineage",
              description:
                "Each version preserves its references, generation context, reviewer, and status.",
            },
            {
              title: "Action record",
              description:
                "Delivery and external actions keep their authorization, state, and result.",
            },
          ].map((item) => (
            <Card key={item.title} title={item.title} description={item.description} />
          ))}
        </div>
      </Section>

      <GlobalCTA />
    </>
  );
}
